import { LaunchRounded } from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import dayjs from "dayjs";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import StatusChip from "../../../../components/chips/StatusChip";
import CustomToolTip from "../../../../components/CustomTooltip";
import CustomTooltipBorder from "../../../../components/CustomTooltipBorder";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import SvgToIcon from "../../../../icons/SvgToIcon";
import {
  ControlStatus,
  JIRA_PRIORITY,
  JIRA_STATUS_CATEGORY,
} from "../../../../utils/constants";
import { daysPastSince } from "../../../../utils/timestamp";
import ControlStatusIndicator from "../../../posture/controls/components/ControlStatusIndicator";
import { getJiraStatusColor } from "../../../posture/controls/components/EntitiesSection/utils";
import { logos } from "../../../stack/environment/icons/iconExport";
import JiraPriorityLabel from "./JiraPriorityLabel";
import LabelChip from "./LabelChip";

export default function IssuesTable({
  issues,
  setSelectedIssue,
  loading,
  filterModel,
  onFilterModelChange,
}) {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const handleControlNavigation = (control_id) => {
    trackEvent(
      `user clicked on go to control ${control_id} from tracking page`
    ).then();
    window.open(`/posture/controls?control_id=${control_id}`, "_blank");
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "desc",
    },
    {
      field: "priority",
      sort: "desc",
    },
    {
      field: "control_status",
      sort: "desc",
    },
  ]);

  const columns = [
    {
      field: "control_id",
      headerName: "Control ID",
      width: 110,
      renderCell: (params) => {
        return (
          <Typography variant="body2">
            {params.value ? `#${params.value}` : "-"}
          </Typography>
        );
      },
    },
    { field: "summary", headerName: "Issue Summary", flex: 1 },
    {
      field: "priority",
      headerName: "Priority",
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [JIRA_PRIORITY.HIGHEST]: 5,
          [JIRA_PRIORITY.HIGH]: 4,
          [JIRA_PRIORITY.MEDIUM]: 3,
          [JIRA_PRIORITY.LOW]: 2,
          [JIRA_PRIORITY.LOWEST]: 1,
          [JIRA_PRIORITY.TRIAGE]: 0,
        };
        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },

      renderCell: (params) => {
        return <JiraPriorityLabel priority={params.value} />;
      },
    },
    {
      field: "status",
      headerName: "Status",
      sortComparator: (v1, v2) => {
        let row1 = v1.status_category;
        let row2 = v2.status_category;
        const statusOrder = {
          [JIRA_STATUS_CATEGORY.TODO]: 2,
          [JIRA_STATUS_CATEGORY.IN_PROGRESS]: 1,
          [JIRA_STATUS_CATEGORY.DONE]: 0,
        };
        const value1 = statusOrder[row1] || 0;
        const value2 = statusOrder[row2] || 0;

        return value1 - value2;
      },
      valueGetter: (params) => {
        return params.row;
      },
      renderCell: (params) => {
        const breezeStatus = params.value.status;
        const statusColor = getJiraStatusColor(
          params.value.status_category,
          colors
        );
        return <StatusChip label={breezeStatus} circleColor={statusColor} />;
      },
    },
    { field: "assignee", headerName: "Assignee", flex: 0.7 },
    {
      field: "created",
      headerName: "Opened for",
      renderCell: (params) => {
        const dateString = params.value.toString();
        const createdData = dayjs(dateString, "DD/MM/YYYY HH:mm:ss").format(
          "DD/MM/YYYY HH:mm"
        );

        return (
          <CustomToolTip
            title={
              <Box display={"flex"} flexDirection={"column"}>
                <Typography variant="caption" fontWeight={500}>
                  Created at
                </Typography>
                <Typography variant="caption">{createdData}</Typography>
              </Box>
            }
          >
            <Typography variant="caption" sx={{ cursor: "default" }}>
              {daysPastSince(params.value) + " Days"}
            </Typography>
          </CustomToolTip>
        );
      },
    },

    {
      field: "labels",
      headerName: "Labels",

      renderCell: (params) => {
        const labels = params.value.filter((label) => label !== "breeze");

        const remainingLabels = labels.slice(2);
        return (
          <Box display={"flex"} flexDirection={"row"} gap={1}>
            {labels.slice(0, 2).map((label, index) => (
              <LabelChip key={index} label={label} />
            ))}
            {labels.length > 3 ? (
              <CustomToolTip
                title={
                  <Box display={"flex"} flexDirection={"column"} gap={"4px"}>
                    {remainingLabels.map((key) => (
                      <Typography variant="caption">{key}</Typography>
                    ))}
                  </Box>
                }
              >
                <Box>
                  <LabelChip label={`+${labels.length - 2}`} />
                </Box>
              </CustomToolTip>
            ) : (
              labels
                .slice(2, 3)
                .map((label, index) => <LabelChip key={index} label={label} />)
            )}
          </Box>
        );
      },
    },
    {
      field: "control_status",
      headerName: "Control Status",
      width: 160,
      sortComparator: (v1, v2) => {
        const statusOrder = {
          [ControlStatus.GAP]: 4,
          [ControlStatus.COMPLIANT]: 3,
          [ControlStatus.SNOOZED]: 2,
          [ControlStatus.EXCLUDED]: 1,
        };

        const value1 = statusOrder[v1] || 0;
        const value2 = statusOrder[v2] || 0;

        return value1 - value2;
      },
      renderCell: (params) => {
        return params.value ? (
          <CustomTooltipBorder
            borderColor={colors.common_white}
            title={
              <Box
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
                onClick={() => handleControlNavigation(params.row.control_id)}
              >
                <Typography
                  variant="caption"
                  sx={{ color: colors.primary_main }}
                >
                  Go To Control #{params.row.control_id}
                </Typography>
                <ArrowForwardIcon
                  sx={{ width: "12px", color: colors.primary_main }}
                />
              </Box>
            }
          >
            <Box>
              <ControlStatusIndicator status={params.value} />
            </Box>
          </CustomTooltipBorder>
        ) : null;
      },
    },

    {
      headerName: "Source",
      field: "source",
      renderCell: (params) => {
        //TODO:replace with generic code that fits other integrations then jira

        return (
          <Box
            sx={{
              display: "flex",
              gap: 1,

              alignItems: "center",
            }}
          >
            <SvgToIcon
              IconLogo={logos.JiraLogo}
              style={{
                width: "20px",
              }}
            />
            <CustomToolTip title={"View in Jira"}>
              <LaunchRounded
                onClick={() => {
                  setSelectedIssue(params.row);
                  window.open(params.value, "_blank");
                }}
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    color: "primary.main",
                  },
                  width: "14px",
                }}
              />
            </CustomToolTip>
          </Box>
        );
      },
    },
  ];

  return (
    <StyledDataGrid
      checkboxSelection={false}
      rows={issues.map((issue) => {
        return {
          ...issue,
          // Fix the type of created so it works with datagrid dateTime type
          created: new Date(issue.created),
        };
      })}
      boxSXProps={{
        "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
          {
            paddingLeft: "24px",
          },
      }}
      columns={columns}
      loading={loading}
      sortModel={sortModel}
      onSortModelChange={(model) => setSortModel(model)}
      pageSizeOptionsOverride={[8, 15, 20]}
    />
  );
}
